import type { LocationPoint } from '../planning.ts'
import type {
  MultimodalArriveByRouteMessage,
  MultimodalMetadata,
  MultimodalRouteMessage,
  MultimodalWorkerMessage,
  MultimodalWorkerRequest,
} from './protocol.ts'
import type { ExactTransfer, StationAccess, StationEgress, TransferPass } from '../transit/router.ts'

type MultimodalDepartAtRequest = Extract<MultimodalWorkerRequest, { type: 'route' }>

interface MultimodalRouteOptions {
  walkingAccessByOrigin?: StationAccess[][]
  transferPass?: TransferPass
  exactTransfers?: ExactTransfer[]
}

export class MultimodalClient {
  readonly ready: Promise<MultimodalMetadata>
  metadata: MultimodalMetadata | null = null
  unavailableReason: string | null = null

  private worker: Worker | null = null
  private requestId = 0
  private readonly pending = new Map<number, {
    resolve: (message: MultimodalRouteMessage | MultimodalArriveByRouteMessage) => void
    reject: (cause: Error) => void
  }>()

  constructor() {
    this.ready = new Promise((resolve, reject) => {
      try {
        this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error('Multimodal routing is unavailable.')
        this.fail(error)
        reject(error)
        return
      }
      this.worker.addEventListener('message', (event: MessageEvent<MultimodalWorkerMessage>) => {
        const message = event.data
        if (message.type === 'ready') {
          this.metadata = message.metadata
          resolve(message.metadata)
        } else if (message.type === 'route' || message.type === 'arrive-by') {
          const pending = this.pending.get(message.requestId)
          if (!pending) return
          this.pending.delete(message.requestId)
          pending.resolve(message)
        } else {
          const error = new Error(message.message)
          if (message.requestId === undefined) {
            this.fail(error)
            reject(error)
          } else {
            this.pending.get(message.requestId)?.reject(error)
            this.pending.delete(message.requestId)
          }
        }
      })
      this.worker.addEventListener('error', () => {
        const error = new Error('The multimodal routing worker stopped unexpectedly.')
        this.fail(error)
        reject(error)
      })
    })
  }

  route(
    date: string,
    departureSeconds: number,
    origins: Pick<LocationPoint, 'lat' | 'lon'>[],
    allowedModesByOrigin: MultimodalDepartAtRequest['allowedModesByOrigin'],
    nonWalkingAccessByOrigin: MultimodalDepartAtRequest['nonWalkingAccessByOrigin'] = origins.map(() => []),
    candidatePass = false,
    options: MultimodalRouteOptions = {},
  ): Promise<MultimodalRouteMessage['results']> {
    if (!this.worker || !this.metadata) {
      return Promise.reject(new Error(this.unavailableReason ?? 'Multimodal schedules are still loading.'))
    }
    this.pending.forEach(({ reject }) => reject(new Error('Superseded by a newer multimodal route request.')))
    this.pending.clear()
    const requestId = ++this.requestId
    const request: MultimodalWorkerRequest = {
      type: 'route',
      requestId,
      date,
      departureSeconds,
      origins,
      allowedModesByOrigin,
      nonWalkingAccessByOrigin,
      ...(candidatePass ? { candidatePass: true } : {}),
      ...options,
    }
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, {
        resolve: (message) => message.type === 'route'
          ? resolve(message.results)
          : reject(new Error('Multimodal routing returned an unexpected response.')),
        reject,
      })
      try {
        this.worker?.postMessage(request)
      } catch (cause) {
        this.pending.delete(requestId)
        reject(cause instanceof Error ? cause : new Error('Multimodal routing could not start.'))
      }
    })
  }

  arriveBy(
    date: string,
    arrivalSeconds: number,
    accessByOrigin: StationAccess[][],
    egressByDestination: StationEgress[][],
    allowedModesByOrigin: Extract<MultimodalWorkerRequest, { type: 'arrive-by' }>['allowedModesByOrigin'],
    options: Pick<MultimodalRouteOptions, 'transferPass' | 'exactTransfers'> = {},
  ): Promise<MultimodalArriveByRouteMessage['results']> {
    if (!this.worker || !this.metadata) {
      return Promise.reject(new Error(this.unavailableReason ?? 'Multimodal schedules are still loading.'))
    }
    this.pending.forEach(({ reject }) => reject(new Error('Superseded by a newer multimodal route request.')))
    this.pending.clear()
    const requestId = ++this.requestId
    const request: MultimodalWorkerRequest = {
      type: 'arrive-by', requestId, date, arrivalSeconds, accessByOrigin,
      egressByDestination, allowedModesByOrigin, ...options,
    }
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, {
        resolve: (message) => message.type === 'arrive-by'
          ? resolve(message.results)
          : reject(new Error('Multimodal routing returned an unexpected response.')),
        reject,
      })
      try {
        this.worker?.postMessage(request)
      } catch (cause) {
        this.pending.delete(requestId)
        reject(cause instanceof Error ? cause : new Error('Multimodal routing could not start.'))
      }
    })
  }

  private fail(error: Error): void {
    this.metadata = null
    this.unavailableReason = error.message
    this.worker?.terminate()
    this.worker = null
    this.pending.forEach(({ reject }) => reject(error))
    this.pending.clear()
  }
}
